import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { querySuiteExecutions, querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { useSuiteOptions } from '@/features/suites';
import { SuiteExecutionsTable } from '@/features/suites/components/SuiteExecutionsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  type CloneExecutionSortDirection,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortKey,
  cloneExecutionStatusOptions,
} from '@/features/suites/pages/suiteExecutionsTableDefinition';
import { SuitesPageClonePanel } from '@/features/suites/pages/SuitesPageClonePanel';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  type CloneSortDirection,
  cloneSortFieldMap,
  type CloneSortKey,
  cloneStatusOptions,
} from '@/features/suites/pages/suitesTableDefinition';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useTagOptions } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  SuiteExecutionStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

type CloneSuitesPanelTab = 'suites' | 'executions';

export const SuitesPage = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading suites...',
    metricsScope: 'suite',
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteExecutions,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<SuiteStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteExecutionSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteExecutionStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          project: true,
          latestExecution: true,
          executionStatusCounts: true,
          executionSummaries: true,
          latestTestResults: { counts: true, summaries: true },
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });
  const { tagOptions } = useTagOptions({ projectId: viewModel.selectedProjectId });
  const { suiteOptions } = useSuiteOptions({ projectId: viewModel.selectedProjectId });
  const initialCloneColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneDefaultColumnVisibilityModel,
  };
  const initialCloneExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneExecutionDefaultColumnVisibilityModel,
  };

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<SuiteExecutionStatus[]>([]);
  const [cloneFilterSuites, setCloneFilterSuites] = useState<string[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneSuitesPanelTab>('suites');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneExecutionColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    SuiteExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneSuitesMeta, setCloneSuitesMeta] = useState<TableQueryMeta<SuiteListItemDto> | null>(
    null,
  );
  const [cloneSuiteExecutionsMeta, setCloneSuiteExecutionsMeta] =
    useState<TableQueryMeta<SuiteExecutionDto> | null>(null);

  const cloneAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('id', cloneFilterSuites),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            project: true,
            latestExecution: true,
            executionStatusCounts: true,
            executionSummaries: true,
            latestTestResults: { counts: true, summaries: true },
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterSuites,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return querySuiteExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suites found for selected date range'
    : formatRangeWindowNoDataText('No suites found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suite executions found for selected date range'
    : formatRangeWindowNoDataText('No suite executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const suiteMetricCards = useMemo(
    () => viewModel.metricCards.filter((card) => card.id !== 'untested'),
    [viewModel.metricCards],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneSuiteFilterKey = useMemo(
    () => [...cloneFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterSuites],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  useEffect(() => {
    if (cloneActiveTab === 'suites') {
      viewModel.handleSearchChange(debouncedCloneSearch);
      viewModel.handleStatusesChange(cloneFilterStatuses as SuiteStatusKey[]);
      viewModel.handleTagsChange(cloneFilterTags);
    } else {
      viewModel.handleSearchChange(debouncedCloneExecutionSearch);
      viewModel.handleStatusesChange(cloneExecutionFilterStatuses as SuiteStatusKey[]);
      viewModel.handleTagsChange(cloneExecutionFilterTags);
    }
    viewModel.handleRangeChange(cloneDateQuickFilter);
    viewModel.handleSelectedStartDayChange(
      cloneFromDate ? cloneFromDate.format('YYYY-MM-DD') : null,
    );
    viewModel.handleSelectedEndDayChange(cloneToDate ? cloneToDate.format('YYYY-MM-DD') : null);
  }, [
    cloneActiveTab,
    cloneDateQuickFilter,
    cloneExecutionFilterStatuses,
    cloneExecutionFilterTags,
    cloneFilterStatuses,
    cloneFilterTags,
    cloneFromDate,
    cloneToDate,
    debouncedCloneExecutionSearch,
    debouncedCloneSearch,
    viewModel,
  ]);
  const cloneSuiteExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneSuiteExecutionsMeta, (item) => item.timestamp),
    [cloneSuiteExecutionsMeta],
  );
  const sharedChartToolbarState = useMemo<
    MetricsStatusChartPanelModelToolbarState<SuiteExecutionStatus>
  >(
    () =>
      cloneActiveTab === 'suites'
        ? {
            search: debouncedCloneSearch,
            setSearch: (value) => {
              handleDebouncedCloneSearchChange(value);
              viewModel.handleSearchChange(value);
            },
            selectedStatuses: cloneFilterStatuses,
            setSelectedStatuses: (next) => {
              setCloneFilterStatuses(next);
              viewModel.handleStatusesChange(next as SuiteStatusKey[]);
            },
            selectedTags: cloneFilterTags,
            setSelectedTags: (next) => {
              setCloneFilterTags(next);
              viewModel.handleTagsChange(next);
            },
            noRowsText: cloneNoRowsText,
            dateQuickFilter: cloneDateQuickFilter,
            onDateQuickFilterChange: (next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
              viewModel.handleRangeChange(next);
              viewModel.handleSelectedStartDayChange(null);
              viewModel.handleSelectedEndDayChange(null);
            },
            fromDate: cloneFromDate,
            toDate: cloneToDate,
            onFromDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneFromDate(nextValue);
              viewModel.handleSelectedStartDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
            onToDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneToDate(nextValue);
              viewModel.handleSelectedEndDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
          }
        : {
            search: debouncedCloneExecutionSearch,
            setSearch: (value) => {
              handleDebouncedCloneExecutionSearchChange(value);
              viewModel.handleSearchChange(value);
            },
            selectedStatuses: cloneExecutionFilterStatuses,
            setSelectedStatuses: (next) => {
              setCloneExecutionFilterStatuses(next);
              viewModel.handleStatusesChange(next as SuiteStatusKey[]);
            },
            selectedTags: cloneExecutionFilterTags,
            setSelectedTags: (next) => {
              setCloneExecutionFilterTags(next);
              viewModel.handleTagsChange(next);
            },
            noRowsText: cloneExecutionsNoRowsText,
            dateQuickFilter: cloneDateQuickFilter,
            onDateQuickFilterChange: (next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
              viewModel.handleRangeChange(next);
              viewModel.handleSelectedStartDayChange(null);
              viewModel.handleSelectedEndDayChange(null);
            },
            fromDate: cloneFromDate,
            toDate: cloneToDate,
            onFromDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneFromDate(nextValue);
              viewModel.handleSelectedStartDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
            onToDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneToDate(nextValue);
              viewModel.handleSelectedEndDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
          },
    [
      cloneActiveTab,
      cloneDateQuickFilter,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionsNoRowsText,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneFromDate,
      cloneNoRowsText,
      cloneToDate,
      debouncedCloneExecutionSearch,
      debouncedCloneSearch,
      handleDebouncedCloneExecutionSearchChange,
      handleDebouncedCloneSearchChange,
    ],
  );
  const cloneContentOverride = (
    <Stack gap="2xl">
      <StackedMetricsStatusChartSection<SuiteExecutionStatus>
        searchAriaLabel={
          cloneActiveTab === 'suites'
            ? 'Search stacked suite chart data'
            : 'Search stacked suite execution chart data'
        }
        noRowsBaseText={
          cloneActiveTab === 'suites' ? 'No suites found' : 'No suite executions found'
        }
        scope="suite"
        projectId={viewModel.selectedProjectId}
        availableTags={
          cloneActiveTab === 'suites' ? cloneAvailableTags : cloneExecutionAvailableTags
        }
        metricCards={suiteMetricCards}
        loading={viewModel.showMetricCardsSkeleton}
        statusCardSkeletonCount={5}
        selectedStatusIds={viewModel.selectedStatuses}
        onStatusToggle={(status) => {
          if (status === 'total') return;
          if (cloneActiveTab === 'suites') {
            setCloneFilterStatuses((prev) =>
              prev.includes(status as SuiteExecutionStatus)
                ? prev.filter((item) => item !== status)
                : [...prev, status as SuiteExecutionStatus],
            );
          } else {
            setCloneExecutionFilterStatuses((prev) =>
              prev.includes(status as SuiteExecutionStatus)
                ? prev.filter((item) => item !== status)
                : [...prev, status as SuiteExecutionStatus],
            );
          }
          viewModel.handleStatusToggle(status as SuiteStatusKey);
        }}
        toolbarState={sharedChartToolbarState}
        statusOptions={
          cloneActiveTab === 'suites' ? cloneStatusOptions : cloneExecutionStatusOptions
        }
        statusFilterLabel="Status"
        suppressAnimation={viewModel.suppressChartAnimation}
        showPoints={viewModel.showLineChartPoints}
      />
      <SuitesPageClonePanel
        cloneActiveTab={cloneActiveTab}
        setCloneActiveTab={setCloneActiveTab}
        cloneDateQuickFilter={cloneDateQuickFilter}
        setCloneDateQuickFilter={setCloneDateQuickFilter}
        cloneFromDate={cloneFromDate}
        setCloneFromDate={setCloneFromDate}
        cloneToDate={cloneToDate}
        setCloneToDate={setCloneToDate}
        cloneColumnVisibilityModel={cloneColumnVisibilityModel}
        setCloneColumnVisibilityModel={setCloneColumnVisibilityModel}
        cloneSortKey={cloneSortKey}
        setCloneSortKey={setCloneSortKey}
        cloneSortDirection={cloneSortDirection}
        setCloneSortDirection={setCloneSortDirection}
        cloneFilterStatuses={cloneFilterStatuses}
        setCloneFilterStatuses={setCloneFilterStatuses}
        cloneFilterSuites={cloneFilterSuites}
        setCloneFilterSuites={setCloneFilterSuites}
        cloneFilterTags={cloneFilterTags}
        setCloneFilterTags={setCloneFilterTags}
        cloneExecutionColumnVisibilityModel={cloneExecutionColumnVisibilityModel}
        setCloneExecutionColumnVisibilityModel={setCloneExecutionColumnVisibilityModel}
        cloneExecutionSortKey={cloneExecutionSortKey}
        setCloneExecutionSortKey={setCloneExecutionSortKey}
        cloneExecutionSortDirection={cloneExecutionSortDirection}
        setCloneExecutionSortDirection={setCloneExecutionSortDirection}
        cloneExecutionFilterStatuses={cloneExecutionFilterStatuses}
        setCloneExecutionFilterStatuses={setCloneExecutionFilterStatuses}
        cloneExecutionFilterTags={cloneExecutionFilterTags}
        setCloneExecutionFilterTags={setCloneExecutionFilterTags}
        cloneAvailableTags={cloneAvailableTags}
        cloneExecutionAvailableTags={cloneExecutionAvailableTags}
        suiteOptions={suiteOptions}
        cloneDateWindow={cloneDateWindow}
        cloneStatusFilterKey={cloneStatusFilterKey}
        cloneSuiteFilterKey={cloneSuiteFilterKey}
        cloneTagFilterKey={cloneTagFilterKey}
        cloneExecutionStatusFilterKey={cloneExecutionStatusFilterKey}
        cloneExecutionTagFilterKey={cloneExecutionTagFilterKey}
        cloneNoRowsText={cloneNoRowsText}
        cloneExecutionsNoRowsText={cloneExecutionsNoRowsText}
        debouncedCloneSearch={debouncedCloneSearch}
        debouncedCloneExecutionSearch={debouncedCloneExecutionSearch}
        cloneSuitesMeta={cloneSuitesMeta}
        setCloneSuitesMeta={setCloneSuitesMeta}
        cloneSuiteExecutionsMeta={cloneSuiteExecutionsMeta}
        setCloneSuiteExecutionsMeta={setCloneSuiteExecutionsMeta}
        cloneSuiteExecutionDateBounds={cloneSuiteExecutionDateBounds}
        showCloneDeltas={showCloneDeltas}
        selectedOrganisationId={viewModel.selectedOrganisationId}
        selectedProjectId={viewModel.selectedProjectId}
        handleSearchChange={viewModel.handleSearchChange}
        handleRangeChange={viewModel.handleRangeChange}
        handleSelectedStartDayChange={viewModel.handleSelectedStartDayChange}
        handleSelectedEndDayChange={viewModel.handleSelectedEndDayChange}
        handleStatusesChange={viewModel.handleStatusesChange}
        handleTagsChange={viewModel.handleTagsChange}
        handleDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
        handleDebouncedCloneExecutionSearchChange={handleDebouncedCloneExecutionSearchChange}
        cloneQueryFn={cloneQueryFn}
        cloneExecutionQueryFn={cloneExecutionQueryFn}
      />
    </Stack>
  );

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle="No suite executions found"
        entityTableNoRowsText="No suites found"
        executionTableNoRowsText="No suite executions found"
        persistKey="suites-table"
        executionPersistKey="suite-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          search,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <SuitesTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            search={search}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, search, persistKey, noRowsText }) => (
          <SuiteExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            search={search}
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
